import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { type FC, useEffect, useState } from "react";
import { ButtonComplete } from "@/components/buttons/ButtonComplete.tsx";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { IconClock } from "@/icons/IconClock.tsx";
import { Row } from "@/components/common/row.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import { toastAndError } from "@/helpers/toast.ts";
import type { UnlockCallback } from "@/components/buttons/shared.ts";
import type { Chat } from "@/stores/useChatStore.ts";
import { Slider } from "@/components/inputs/Slider.tsx";
import { Column } from "@/components/common/column.ts";
import { putTutorSocrateRating } from "@/api/tutors.ts";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { Checkbox } from "@/components/inputs/Checkbox.tsx";
import { BasicInputSmall } from "@/components/inputs/shared.ts";
import type { QuizSessionDetailsResponse } from "@/api/quiz.ts";
import type { FlowGeneratorSession } from "@/types/flow.ts";
import { AnswerToExplanation } from "@/helpers/answerMapper.ts";
import { interventionStorageCollection } from "@/collections/interventionStorageCollection.ts";

const ChatFeedbackWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  background: ${t.chatFooterBackground};
`;

const Gap = "0px";

const Feedback = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Gap};
  padding: 16px;
  background: white;
  margin: 20px ${t.chatPaddingSides};
  border-radius: ${t.brandBorderRadius};
  min-width: 600px;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Gap};
  overflow-x: scroll;
  scrollbar-width: none;
`;

const ButtonRow = styled(Row)`
  display: flex;
  justify-self: flex-end;
  margin: 12px ${t.chatPaddingSides};
`;

const SliderFeedback = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 500px;
`;

const SliderRotate = styled.div`
  display: flex;
  justify-content: center;
  align-items: start;
  rotate: 270deg;
  width: 10rem;
  height: 10rem;
`;

const FeedbackText = styled.p`
  text-align: right;

  cursor: pointer;
  user-select: none;
  transition: all 150ms ease;

  &[data-selected="true"] {
    color: ${t.brandPastelBlue};
    scale: 1;
  }

  &[data-selected="false"] {
    color: ${t.brandNeutralGreyDark};
    scale: 0.9;
  }
`;

const MainFeedback = styled.div`
  width: stretch;
  min-width: 500px;
`;

const SliderPadding = styled.div`
  padding: 0 0.25rem;
`;

interface ChatFeedbackProps {
  interventionId?: number;
  closeFeedback: () => void;
  chat: Chat;
  flowQuizSession?: QuizSessionDetailsResponse;
}

export const ChatFeedback: FC<ChatFeedbackProps> = ({
  chat,
  flowQuizSession,
  interventionId,
  closeFeedback,
}) => {
  const { tutorId } = useUserStore();
  const { SetSessionFeedbackGiven } = useAssignedChatStore();
  const [socrateRating, setSocrateRating] = useState<number>(3); // 1-5
  const [overallRating, setOverallRating] = useState<number>(2); // 0-4
  const [wow, setWow] = useState<boolean>(false);
  const [negativeFeedback, setNegativeFeedback] = useState<string>();
  const [positiveFeedback, setPositiveFeedback] = useState<string>();

  // Note: Forcing type as not undefined because ChatFeedback only renders if it's valid
  const flowSession = chat.flowGeneratorSession as FlowGeneratorSession;

  const questionNo = flowSession.flowGeneratorSession.flowItemGroupInputs.filter(
    (data) =>
      data.flowItemGroup.includes("CheckInQuestion_") &&
      !data.flowItemGroup.includes("_InterventionRating"),
  ).length;

  const currentQuestion = flowQuizSession?.questions.find((q) => q.sequence === questionNo);

  useEffect(() => {
    if (!interventionId) {
      closeFeedback();
    }
  }, [closeFeedback, interventionId]);

  if (!interventionId) {
    return <></>;
  }

  const SubmitFeedback = (unlockCallback: UnlockCallback) => {
    putTutorSocrateRating(
      tutorId,
      interventionId,
      overallRating,
      wow,
      positiveFeedback,
      negativeFeedback,
      socrateRating,
    )
      .then(() => {
        SetSessionFeedbackGiven(chat.flowGeneratorSessionId);
        closeFeedback();
      })
      .catch((e) => toastAndError("Could not update feedback", e))
      .finally(() => {
        unlockCallback();
      });
  };

  const FinishSession = () => {
    closeFeedback();
  };

  const fillColor = () => {
    switch (socrateRating) {
      case 5:
      case 4:
        return t.brandAccentRed;
      case 3:
        return t.brandAccentYellow;
      case 2:
      case 1:
        return t.brandAccentGreen;
      default:
        return t.sliderTrackedColor;
    }
  };

  const isAllowedAi = !(
    chat.socrates.session?.learnerSessionId === undefined &&
    interventionStorageCollection.get(chat.flowGeneratorSessionId)?.lastSocratesSessionId === ""
  );

  return (
    <ChatFeedbackWrapper>
      <Feedback>
        <Main>
          <h1>Give your feedback</h1>
          <br />

          <Row data-justify={"between"} data-width={"full"}>
            {!isAllowedAi ? (
              <MainFeedback>
                <Column>
                  <Checkbox
                    id={"wow"}
                    state={wow}
                    onClick={() => setWow(!wow)}
                    label={"Did this session make you go 'Wow?'"}
                  />
                  <BasicInputSmall
                    value={positiveFeedback ?? ""}
                    placeholder={
                      "What worked particularly well - especially things AI couldn't have helped with?"
                    }
                    onChange={(e) => setPositiveFeedback(e.target.value)}
                  />
                  <BasicInputSmall
                    value={negativeFeedback ?? ""}
                    placeholder={"Where (if anywhere) could AI suggestions have helped?"}
                    onChange={(e) => setNegativeFeedback(e.target.value)}
                  />
                </Column>
              </MainFeedback>
            ) : (
              <MainFeedback>
                <p>
                  How would you rate the overall quality of the AI-generated messages in this
                  session?
                </p>
                <SliderPadding>
                  <Slider
                    id={"overallRating"}
                    state={overallRating}
                    min={0}
                    max={4}
                    onChange={(newNumber) => setOverallRating(newNumber)}
                  />
                </SliderPadding>
                <Row data-justify={"between"} data-align={"center"}>
                  <FeedbackText
                    data-selected={overallRating === 0}
                    onClick={() => setOverallRating(0)}
                  >
                    Poor
                  </FeedbackText>
                  <FeedbackText
                    data-selected={overallRating === 1}
                    onClick={() => setOverallRating(1)}
                  >
                    Fairly Poor
                  </FeedbackText>
                  <FeedbackText
                    data-selected={overallRating === 2}
                    onClick={() => setOverallRating(2)}
                  >
                    Average
                  </FeedbackText>
                  <FeedbackText
                    data-selected={overallRating === 3}
                    onClick={() => setOverallRating(3)}
                  >
                    Fairly Good
                  </FeedbackText>
                  <FeedbackText
                    data-selected={overallRating === 4}
                    onClick={() => setOverallRating(4)}
                  >
                    Good
                  </FeedbackText>
                </Row>
                <br />
                <Column>
                  <Checkbox
                    id={"wow"}
                    state={wow}
                    onClick={() => setWow(!wow)}
                    label={"Did this intervention make you go 'Wow?'"}
                  />
                  <BasicInputSmall
                    value={negativeFeedback ?? ""}
                    placeholder={"Any negative feedback?"}
                    onChange={(e) => setNegativeFeedback(e.target.value)}
                  />
                  <BasicInputSmall
                    value={positiveFeedback ?? ""}
                    placeholder={"Any positive/Neutral feedback?"}
                    onChange={(e) => setPositiveFeedback(e.target.value)}
                  />
                </Column>
              </MainFeedback>
            )}

            {currentQuestion && (
              <SliderFeedback>
                <p>
                  To what extent did the student demonstrate this misconception:
                  <br />
                  {AnswerToExplanation(
                    currentQuestion.answers.findLast(() => true)?.answerValue ?? 0,
                    currentQuestion,
                  )}
                </p>
                <Row data-justify={"center"}>
                  <Column data-justify={"between"} data-align={"start"}>
                    <FeedbackText
                      data-selected={socrateRating === 5}
                      onClick={() => setSocrateRating(5)}
                    >
                      Rejected: Misconception clearly absent
                    </FeedbackText>
                    <FeedbackText
                      data-selected={socrateRating === 4}
                      onClick={() => setSocrateRating(4)}
                    >
                      Leaning toward absent
                    </FeedbackText>
                    <FeedbackText
                      data-selected={socrateRating === 3}
                      onClick={() => setSocrateRating(3)}
                    >
                      Insufficient evidence
                    </FeedbackText>
                    <FeedbackText
                      data-selected={socrateRating === 2}
                      onClick={() => setSocrateRating(2)}
                    >
                      Leaning toward present
                    </FeedbackText>
                    <FeedbackText
                      data-selected={socrateRating === 1}
                      onClick={() => setSocrateRating(1)}
                    >
                      Confirmed: Misconception was present
                    </FeedbackText>
                  </Column>
                  <SliderRotate>
                    <Slider
                      id={"socratesRating"}
                      state={socrateRating}
                      min={1}
                      max={5}
                      onChange={(newNumber) => {
                        setSocrateRating(newNumber);
                      }}
                      fillColor={fillColor()}
                      trackColor={fillColor()}
                      fillFromMiddle={true}
                    />
                  </SliderRotate>
                </Row>
              </SliderFeedback>
            )}
          </Row>
        </Main>
      </Feedback>
      <ButtonRow>
        <ButtonIcon
          backgroundColor={"#292D66E5"}
          icon={<IconClock color={"white"} />}
          text={"I'll do it later"}
          onClick={(unlock) => {
            FinishSession();
            unlock();
          }}
          flare
        />
        <ButtonComplete
          text={"Submit feedback"}
          onClick={(unlockCallback) => SubmitFeedback(unlockCallback)}
          flare
        />
      </ButtonRow>
    </ChatFeedbackWrapper>
  );
};
